// ── Optimization Center: Revision Suggestions Module ─────────────
// Owns: step-revision panel DOM, suggestion generation and apply.

import {
  getState,
  setState,
  setStateBulk,
  subscribe,
} from './state.js';
import { showAuthModal } from './app/auth-controller.js';
import { diagnosticFacade } from './interaction/facades/diagnostic.facade.instance.js';
import { listingFacade } from './interaction/facades/listing.facade.instance.js';
import { collectCurrentListing, resolveSnapshotAsinsForApi } from './listing-utils.js';
import {
  LISTING_TEXT_REVISION_PROTOCOL,
  buildListingRevisionTextHash,
  normalizeRevisionAdviceText,
  normalizeSearchTermsDraft,
  renderListingRevisionText,
} from './listing-draft-payload.js';
import { syncNavigationBounds } from './navigation-bounds.js';
import { navigateToStep } from './navigation.js';
import { showToast } from './toast.js';
import { packKeywordsByBytes, updateByteCounter } from './byte-counter.js';
import {
  getMarketDataKeywords,
  buildVolumeMap,
  enrichKeywordsWithVolume,
  extractKeywordText,
  extractSearchVolume,
} from './keyword-lookup.js';
import { renderKeywordChips } from './keyword-chips.js';
import { ensureStep3AutoSave } from './step3/autosave.js';
import { renderCandidatePreviewInto } from './presentation/steps/step6/candidate-preview.js';
import { renderRevisionFactQuestionsInto } from './presentation/steps/step6/fact-questions.js';
import { renderRevisionSuggestionsEmptyInto } from './presentation/steps/step6/suggestion-empty-state.js';
import { renderRevisionDiffNoticeInto } from './presentation/steps/step6/writeback-diff-notice.js';
import { renderRevisionSuggestionCardsInto } from './presentation/steps/step6/suggestion-cards.js';
import { createRevisionCandidateController } from './step6/revision-candidate-controller.js';
import {
  createRevisionDiffController,
  deriveRevisionChangedFields,
} from './step6/revision-diff-controller.js';
import { createRevisionMasterInstructionsController } from './step6/revision-master-instructions-controller.js';
import { createRevisionSearchTermsEditorController } from './step6/search-terms-editor-controller.js';
import { createRevisionSuggestionsController } from './step6/revision-suggestions-controller.js';
import { createRevisionInitializationController } from './step6/revision-initialization-controller.js';
import { createRevisionApplyController } from './step6/revision-apply-controller.js';
import {
  appendClientTraceCalls,
  formatRevisionUserFacingReason,
  renderRevisionExecutionStatus as renderExecutionStatus,
} from './step6/revision-execution-status.js';
import { createRevisionKeywordPolicy } from './step6/revision-keyword-policy.js';
import { buildStep6ImageSuiteUrl, buildStep6VideoSuiteUrl, createRevisionSuiteNavigation } from './step6/revision-suite-navigation.js';
import { createRevisionWritebackController } from './step6/revision-writeback-controller.js';
import {
  buildRevisionCandidateRecord,
  buildRevisionWritebackMeta,
  getInvalidRevisionChangedFields,
  getRevisionAuditBlockingIssue,
  getTextRevisionWritebackDecision,
  isValidRevisionSearchTerms,
  mergeRevisionDraftByChangedFields,
} from './step6/revision-writeback-contract.js';
import {
  buildRevisionFactQuestions,
  createRevisionFactsController,
  getRevisionExecutionAuthority,
  normalizeRevisionSupplementalFacts,
} from './step6/revision-facts-controller.js';
import {
  buildEffectiveRevisionAdviceText,
  buildMasterRevisionAdviceText,
  compactRevisionGenerationLedger,
  buildRevisionPayload,
} from './step6/revision-request-builder.js';

export {
  buildRevisionFactQuestions,
  buildEffectiveRevisionAdviceText,
  buildMasterRevisionAdviceText,
  compactRevisionGenerationLedger,
  buildRevisionPayload,
  buildRevisionCandidateRecord,
  buildRevisionWritebackMeta,
  deriveRevisionChangedFields,
  getInvalidRevisionChangedFields,
  getRevisionExecutionAuthority,
  getRevisionAuditBlockingIssue,
  getTextRevisionWritebackDecision,
  isValidRevisionSearchTerms,
  mergeRevisionDraftByChangedFields,
  normalizeRevisionSupplementalFacts,
};
export { formatRevisionUserFacingReason };
export { buildStep6ImageSuiteUrl, buildStep6VideoSuiteUrl };

// ── DOM Helpers ─────────────────────────────────────────────────

const $ = (sel) => document.querySelector(sel);

// ── Revision Selection State ────────────────────────────────────

let revisionApplyController = null;
const APPLY_REVISION_BUTTON_LABEL = '生成优化稿';

// ── Toast Helper ───────────────────────────────────────────────

function showRevisionToast(msg, isError = false) {
  // 沿用 step6 历史样式：右下、最大宽 400、4 秒
  showToast(msg, {
    id: 'revision-step-toast',
    type: isError ? 'error' : 'info',
    duration: 4000,
    maxWidth: 400,
  });
}

function renderRevisionExecutionStatus(status) {
  renderExecutionStatus($, status);
}

function renderRevisionCandidatePreview(candidateRecord = null) {
  revisionCandidateController.renderPreview(candidateRecord);
}

function clearPendingRevisionCandidate({ clearHistory = false } = {}) {
  revisionCandidateController.clear({ clearHistory });
}

function cancelPendingRevisionCandidate() {
  revisionCandidateController.cancel();
}

function previousRevisionCandidate() {
  revisionCandidateController.previous();
}

function confirmPendingRevisionCandidate() {
  revisionCandidateController.confirm();
}

export function collectMasterRevisionInstructions() {
  // general 只传一次，后端只把它作为已授权字段的辅助要求；Search Terms
  // 始终由代码处理，不再把“整体语气”等指令重复塞进后台搜索词。
  return revisionMasterInstructionsController.collect();
}

function getRevisionMode() {
  return $('#revision-full-rewrite')?.checked ? 'full_rewrite' : 'surgical';
}

function getRevisionBaselineQuality(revisionSuggestionsMeta = null) {
  const raw = revisionSuggestionsMeta?.baselineQuality;
  if (!raw || typeof raw !== 'object') return null;
  const score = Number(raw.overallScore ?? raw.score);
  return {
    overallScore: Number.isFinite(score) ? score : null,
    protectionTier: raw.protectionTier === 'high_score' ? 'high_score' : 'standard',
  };
}

function collectRevisionSupplementalFacts() {
  return revisionFactsController.collectSupplementalFacts();
}

function renderRevisionFactPanel() {
  revisionFactsController.render();
}

function syncApplyRevisionButtonState() {
  const applyBtn = $('#apply-revision-button');
  if (!applyBtn) return;
  const applyInFlight = revisionApplyController?.isInFlight() || false;
  if (!applyInFlight) {
    applyBtn.textContent = revisionCandidateController.hasPending() ? '继续优化候选稿' : APPLY_REVISION_BUTTON_LABEL;
  }
  applyBtn.disabled = !canApplyRevision({
    revisionAdviceText: getState('revisionAdviceText') || '',
    revisionSuggestionsMeta: getState('revisionSuggestionsMeta'),
    currentMarketplace: getState('marketplace'),
    diagnosticMarketplace: getState('diagnosticMarketplace'),
    masterInstructions: collectMasterRevisionInstructions(),
    supplementalFacts: collectRevisionSupplementalFacts(),
    applyInFlight,
    diagnosisInFlight: getState('diagnosisInFlight'),
  });
}

export function canApplyRevision({
  revisionAdviceText = '',
  revisionSuggestionsMeta = null,
  currentMarketplace = '',
  diagnosticMarketplace = '',
  masterInstructions = {},
  supplementalFacts = [],
  applyInFlight = false,
  diagnosisInFlight = false,
} = {}) {
  if (applyInFlight || diagnosisInFlight) return false;
  const currentMarket = String(currentMarketplace || '').trim().toUpperCase();
  const reviewedMarket = String(
    diagnosticMarketplace || revisionSuggestionsMeta?.marketplace || ''
  ).trim().toUpperCase();
  const hasUserInstructions = Object.values(masterInstructions || {})
    .some((value) => Boolean(normalizeRevisionAdviceText(value)));
  if (!currentMarket) return false;
  if (reviewedMarket && currentMarket !== reviewedMarket) return false;
  const authority = getRevisionExecutionAuthority(revisionSuggestionsMeta);
  if (hasUserInstructions) return true;
  if (!reviewedMarket || !authority.actionable || !authority.conclusion) return false;
  return Boolean(normalizeRevisionAdviceText(revisionAdviceText));
}

const revisionSuiteNavigation = createRevisionSuiteNavigation({
  getState,
  select: $,
  ensureAutoSave: ensureStep3AutoSave,
  showToast: showRevisionToast,
});
export const openImageSuiteForCurrentListing = () => revisionSuiteNavigation.openImageSuiteForCurrentListing();

// ── Draft hash helpers (检测旧建议是否仍匹配当前草稿) ──────────
// 只用于"提示"，不阻断流程；目的是防止 Step03 已经手动改过、
// 还应用旧建议覆盖回旧问题的体验问题。
export function getCurrentDraftRevisionKey() {
  const listing = collectCurrentListing({ full: true, useRevisionSearchTermsDraft: true });
  return buildListingRevisionTextHash(renderListingRevisionText(listing));
}

export function buildRevisionDiffItems(nextDraft) {
  return revisionDiffController.buildItems(nextDraft);
}

function renderRevisionDiffNotice(items = [], execution = {}) {
  revisionDiffController.renderNotice(items, execution);
}

/**
 * 检测改稿结果是否与当前草稿有实质差异。
 * 用于「无变化时不应用 + 提示」的兜底，避免后端 echo 回原文还触发跳转。
 */
function hasMeaningfulDiff(nextDraft) {
  return revisionDiffController.hasMeaningfulDiff(nextDraft);
}

// ── Search Terms Editor (Bubble UI) ─────────────────────────────

const MAX_SEARCH_BYTES = 250;

const revisionKeywordPolicy = createRevisionKeywordPolicy({ getState });
export const getKeywordUsePoliciesForStep6 = () => revisionKeywordPolicy.getPolicies();
export const findKeywordPolicy = (keyword) => revisionKeywordPolicy.find(keyword);
/** 推荐区 / 同步入口的轻量过滤：后端 resolver/cleaner 仍是最终事实源。 */
export const isAllowedForSearchTerms = (keyword) => revisionKeywordPolicy.isAllowedForSearchTerms(keyword);

// 注：getMarketDataKeywords / buildVolumeMap / enrichKeywordsWithVolume 已统一抽到 keyword-lookup.js

const revisionFactsController = createRevisionFactsController({
  select: $,
  getState,
  getListingText: () => renderListingRevisionText(collectCurrentListing({ full: true })),
  renderQuestions: renderRevisionFactQuestionsInto,
});

const revisionCandidateController = createRevisionCandidateController({
  select: $,
  setState,
  renderCandidatePreviewInto,
  renderExecutionStatus: renderRevisionExecutionStatus,
  showToast: showRevisionToast,
  writeRevisionToStep03,
  appendClientTraceCalls,
  renderRevisionFactPanel,
  syncApplyButton: syncApplyRevisionButtonState,
});

const revisionMasterInstructionsController = createRevisionMasterInstructionsController({
  getState,
  setState,
  getElementById: (id) => document.getElementById(id),
  onStateChanged: syncApplyRevisionButtonState,
});

const revisionDiffController = createRevisionDiffController({
  getCurrentDraft: () => ({
    title: getState('listingDraftTitle'),
    bullets: getState('listingDraftBullets'),
    description: getState('listingDraftDescription'),
    searchTerms: getState('listingDraftSearchTerms'),
    qa: getState('listingDraftQa'),
  }),
  renderNotice: (items) => renderRevisionDiffNoticeInto(items, {
    existing: $('#step3-revision-diff-notice'),
    reviewStrip: $('#step3-review-strip'),
    generationRoot: $('#step-generation'),
  }),
});

const revisionSearchTermsController = createRevisionSearchTermsEditorController({
  select: $,
  getState,
  setState,
  normalizeSearchTermsDraft,
  enrichKeywordsWithVolume,
  renderKeywordChips,
  getMarketDataKeywords,
  buildVolumeMap,
  extractKeywordText,
  extractSearchVolume,
  updateByteCounter,
  packKeywordsByBytes,
  isAllowedForSearchTerms,
  maxSearchBytes: MAX_SEARCH_BYTES,
  onByteLimitExceeded: (droppedCount) => showRevisionToast(`Search Terms 已自动控制在 ${MAX_SEARCH_BYTES} 字节内，已移除尾部 ${droppedCount} 个超限词。`, true),
  onStateChanged: syncApplyRevisionButtonState,
});

function ensureKeywordsWithVolume(searchTerms) {
  return revisionSearchTermsController.ensureKeywordsWithVolume(searchTerms);
}

function setupSearchTermsEditor() {
  revisionSearchTermsController.setup();
}

function clearStepPanelsAfterRevisionApply() {
  const complianceResult = $('#compliance-result');
  if (complianceResult) complianceResult.hidden = true;
  const complianceViolations = $('#compliance-violations');
  if (complianceViolations) complianceViolations.replaceChildren();
  const algorithmTips = $('#algorithm-tips');
  if (algorithmTips) algorithmTips.hidden = true;
  const algorithmTipsContent = $('#algorithm-tips-content');
  if (algorithmTipsContent) algorithmTipsContent.replaceChildren();

  const scoringResult = $('#scoring-result');
  if (scoringResult) scoringResult.hidden = true;
  const scoringTotal = $('#scoring-total');
  if (scoringTotal) scoringTotal.replaceChildren();
  const scoringDimensions = $('#scoring-dimensions');
  if (scoringDimensions) scoringDimensions.replaceChildren();
  const scoringSuggestions = $('#scoring-suggestions');
  if (scoringSuggestions) scoringSuggestions.replaceChildren();

  const revisionResult = $('#revision-result');
  if (revisionResult) revisionResult.hidden = true;
  const revisionList = $('#revision-suggestions-list');
  if (revisionList) revisionList.replaceChildren();
  setState('revisionOverview', null);
}

const revisionWritebackController = createRevisionWritebackController({
  getState,
  setStateBulk,
  ensureKeywordsWithVolume,
  buildDiffItems: buildRevisionDiffItems,
  buildWritebackMeta: buildRevisionWritebackMeta,
  navigateToStep,
  getElementById: (id) => document.getElementById(id),
  clearOptimizationPanels: clearStepPanelsAfterRevisionApply,
  applyGenerationResult: (result) => listingFacade.applyGenerationResult(result),
  syncNavigationBounds,
  renderDiffNotice: renderRevisionDiffNotice,
});

function writeRevisionToStep03(normalized, stepReady, execution = {}) {
  return revisionWritebackController.writeToStep03(normalized, stepReady, execution);
}

/**
 * 阶段 5：把 Search Terms 类型建议卡里的关键词一键采纳到编辑器。
 *
 * 提取策略：suggestion 文本可能是 "noise cancelling, bluetooth" 或单个词，
 * 用 [，,/、; ；] 拆分后逐个过滤（policy + 长度 + 已存在）；
 * 写入时优先用 keywordLibrary 里的真实搜索量，找不到记 0。
 *
 * @returns {string[]} 实际被加入的关键词列表（去重 + 过滤后）
 */
export function addSearchTermFromSuggestion(keyword) {
  return revisionSearchTermsController.addFromSuggestion(keyword);
}

const revisionSuggestionsController = createRevisionSuggestionsController({
  select: $,
  getState,
  renderEmpty: renderRevisionSuggestionsEmptyInto,
  renderCards: renderRevisionSuggestionCardsInto,
  syncApplyButton: syncApplyRevisionButtonState,
  addSearchTerm: addSearchTermFromSuggestion,
  showToast: showRevisionToast,
});

revisionApplyController = createRevisionApplyController({
  select: $,
  getElementById: (id) => document.getElementById(id),
  getState,
  setState,
  getPendingCandidate: () => revisionCandidateController.getPending(),
  clearPendingCandidate: (options) => revisionCandidateController.clear(options),
  restoreCandidate: (candidate) => revisionCandidateController.restore(candidate),
  pushCandidate: (candidate) => revisionCandidateController.push(candidate),
  getCandidateVersionCount: () => revisionCandidateController.getVersionCount(),
  collectMasterInstructions: collectMasterRevisionInstructions,
  collectSupplementalFacts: collectRevisionSupplementalFacts,
  getRevisionMode,
  getRevisionBaselineQuality,
  getCurrentDraftRevisionKey,
  canApplyRevision,
  collectCurrentListing,
  resolveSnapshotAsinsForApi,
  normalizeRevisionAdviceText,
  buildEffectiveRevisionAdviceText,
  buildRevisionPayload,
  compactRevisionGenerationLedger,
  buildListingRevisionTextHash,
  renderListingRevisionText,
  revisionProtocol: LISTING_TEXT_REVISION_PROTOCOL,
  diagnosticFacade,
  appendClientTraceCalls,
  formatRevisionUserFacingReason,
  renderExecutionStatus: renderRevisionExecutionStatus,
  showToast: showRevisionToast,
  getTextRevisionWritebackDecision,
  normalizeRevisionSupplementalFacts,
  renderFactPanel: renderRevisionFactPanel,
  deriveRevisionChangedFields,
  getInvalidRevisionChangedFields,
  mergeRevisionDraftByChangedFields,
  buildRevisionDiffItems,
  getRevisionAuditBlockingIssue,
  buildRevisionCandidateRecord,
  renderCandidatePreview: renderRevisionCandidatePreview,
  hasMeaningfulDiff,
  syncApplyButton: syncApplyRevisionButtonState,
  onAuthenticationRequired: showAuthModal,
  applyButtonLabel: APPLY_REVISION_BUTTON_LABEL,
});

const revisionInitializationController = createRevisionInitializationController({
  select: $,
  setState,
  subscribe,
  applyRevisionSuggestions: () => { void revisionApplyController.applyRevisionSuggestions(); },
  openImageSuite: () => { void openImageSuiteForCurrentListing(); },
  getRevisionMode,
  showToast: showRevisionToast,
  confirmCandidate: confirmPendingRevisionCandidate,
  cancelCandidate: cancelPendingRevisionCandidate,
  previousCandidate: previousRevisionCandidate,
  suggestionsController: revisionSuggestionsController,
  masterInstructionsController: revisionMasterInstructionsController,
  renderFactPanel: renderRevisionFactPanel,
  syncApplyButton: syncApplyRevisionButtonState,
  normalizeAdviceText: normalizeRevisionAdviceText,
  normalizeSupplementalFacts: normalizeRevisionSupplementalFacts,
  getState,
  setupSearchTermsEditor,
});

// ── Module Init ─────────────────────────────────────────────────

export function initStep6() {
  revisionInitializationController.bind();
}
